import {
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  FaceSmileIcon
} from "@heroicons/react/24/outline"
import { useQueryClient } from "@tanstack/react-query"
import { LoversApi } from "api/LoversApi"
import { WithSimpleLogoHeader } from "components/WithSimpleLogoHeader"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    const verify = async () => {
      const code = searchParams.get("code")

      if (!code) {
        setError("Verification code is missing")
        setIsLoading(false)
        return
      }

      try {
        const res = await LoversApi().verifyEmail.verifyEmail({ verificationCode: code })
        if (res.data.verified) {
          queryClient.invalidateQueries({ queryKey: ["useUser"] })
        } else {
          throw new Error("Email verification failed")
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed")
      } finally {
        setIsLoading(false)
      }
    }

    verify()
  }, [searchParams])

  const handleGoToLogin = () => {
    navigate("/login")
  }

  if (isLoading) {
    return (
      <WithSimpleLogoHeader>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <FaceSmileIcon className="animate-spin h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Verifying email...</h2>
          </div>
        </div>
      </WithSimpleLogoHeader>
    )
  }

  if (error) {
    return (
      <WithSimpleLogoHeader>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Verification Failed
            </h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <button
              onClick={handleGoToLogin}
              className="mt-6 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
            >
              Go to Login
            </button>
          </div>
        </div>
      </WithSimpleLogoHeader>
    )
  }

  return (
    <WithSimpleLogoHeader>
      <div className="flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckBadgeIcon className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Email Verified!</h2>
          <p className="mt-2 text-gray-600">Your email has been successfully verified</p>
          <button
            onClick={handleGoToLogin}
            className="mt-6 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          >
            Go to Login
          </button>
        </div>
      </div>
    </WithSimpleLogoHeader>
  )
}
